import React from "react";
import { Alert, Col, Container, Row, Spinner } from "reactstrap";

import {
  getMyUserRanking,
  getUserRankings,
  RankingPeriod,
} from "../../../helpers/api/ranking";
import type { UserRankingItem } from "../../../types/api.generated";

export const LEADERBOARD_PAGE_SIZE = 20;

type PeriodFilter = "overall" | RankingPeriod;

export type LeaderboardUser = UserRankingItem;

const periodOptions: Array<{ key: PeriodFilter; label: string }> = [
  { key: "overall", label: "Overall" },
  { key: "weekly", label: "This week" },
  { key: "monthly", label: "This month" },
];

export const getRankingPeriodParam = (period: PeriodFilter): RankingPeriod | undefined => {
  return period === "overall" ? "all_time" : period;
};

export const getRank = (user?: LeaderboardUser) => Number(user?.rank ?? 0);

export const getDisplayName = (user?: LeaderboardUser) => {
  return user?.username || "Unknown player";
};

export const getNetResult = (user?: LeaderboardUser) => {
  return Number(user?.netResult ?? 0);
};

export const getWinRate = (user?: LeaderboardUser) => {
  const value = Number(user?.winRate ?? 0);
  return value > 0 && value <= 1 ? value * 100 : value;
};

export const getBetCount = (user?: LeaderboardUser) => {
  return Number(user?.betCount ?? 0);
};

const formatInteger = (value: number) => {
  return new Intl.NumberFormat("fr-FR").format(Math.round(value));
};

const formatSigned = (value: number) => {
  const rounded = Math.round(value);
  return `${rounded > 0 ? "+" : ""}${formatInteger(rounded)}`;
};

const getRankTone = (rank: number) => {
  if (rank === 1) return " leaderboard-row--gold";
  if (rank === 2) return " leaderboard-row--silver";
  if (rank === 3) return " leaderboard-row--bronze";
  return "";
};

const PlayerAvatar = ({ user }: { user: LeaderboardUser }) => {
  const name = getDisplayName(user);
  const initial = name.trim().charAt(0).toUpperCase() || "?";

  return (
    <div className="leaderboard-avatar">
      <span aria-hidden="true">{initial}</span>
    </div>
  );
};

const RankingMetric = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="leaderboard-metric">
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const MyRankingCard = ({ ranking }: { ranking: LeaderboardUser | null }) => {
  if (!ranking) {
    return (
      <section className="leaderboard-me-card leaderboard-me-card--empty" data-cy="leaderboard-me-card">
        <div>
          <span>Your position</span>
          <strong>Unranked</strong>
        </div>
        <p>Place a bet to enter the arena ranking.</p>
      </section>
    );
  }

  const net = getNetResult(ranking);
  const rank = getRank(ranking);

  return (
    <section className="leaderboard-me-card" data-cy="leaderboard-me-card">
      <div className="leaderboard-me-card__rank">
        <span>Your rank</span>
        <strong>#{rank || "-"}</strong>
      </div>

      <div className="leaderboard-me-card__identity">
        <PlayerAvatar user={ranking} />
        <div>
          <strong>{getDisplayName(ranking)}</strong>
          <span>{formatSigned(net)} NET</span>
        </div>
      </div>

      <div className="leaderboard-me-card__stats">
        <RankingMetric label="Net" value={<span className={net >= 0 ? "is-positive" : "is-negative"}>{formatSigned(net)}</span>} />
        <RankingMetric label="Win rate" value={`${Math.round(getWinRate(ranking))}%`} />
        <RankingMetric label="Bets" value={formatInteger(getBetCount(ranking))} />
      </div>
    </section>
  );
};

const LeaderboardRow = ({ user }: { user: LeaderboardUser }) => {
  const rank = getRank(user);
  const net = getNetResult(user);
  const winRate = Math.max(0, Math.min(100, getWinRate(user)));

  return (
    <article className={`leaderboard-row${getRankTone(rank)}`} data-cy="leaderboard-row" data-testid="leaderboard-row">
      <div className="leaderboard-row__rank">
        <span>{rank || "-"}</span>
      </div>

      <div className="leaderboard-row__player">
        <PlayerAvatar user={user} />
        <strong>{getDisplayName(user)}</strong>
      </div>

      <div className="leaderboard-row__points">
        <span>Net result</span>
        <strong className={net >= 0 ? "is-positive" : "is-negative"}>{formatSigned(net)}</strong>
      </div>

      <div className={`leaderboard-row__net${net >= 0 ? " is-positive" : " is-negative"}`}>
        <span>Net</span>
        <strong>{formatSigned(net)}</strong>
      </div>

      <div className="leaderboard-row__rate">
        <span>{Math.round(winRate)}%</span>
        <div aria-label={`Win rate ${Math.round(winRate)}%`}>
          <i style={{ width: `${winRate}%` }} />
        </div>
      </div>

      <div className="leaderboard-row__bets">
        <span>Bets</span>
        <strong>{formatInteger(getBetCount(user))}</strong>
      </div>
    </article>
  );
};

const Leaderboard = () => {
  document.title = "Legends | The Colosseum - Leaderboard";

  const [period, setPeriod] = React.useState<PeriodFilter>("overall");
  const [users, setUsers] = React.useState<LeaderboardUser[]>([]);
  const [myRanking, setMyRanking] = React.useState<LeaderboardUser | null>(null);
  const [page, setPage] = React.useState(1);
  const [total, setTotal] = React.useState<number | undefined>(undefined);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  const loadingRef = React.useRef(false);
  const hasUserScrolledRef = React.useRef(false);

  const hasMore = typeof total === "number" ? users.length < total : users.length === 0 || users.length >= page * LEADERBOARD_PAGE_SIZE;

  const loadLeaderboard = React.useCallback(async (nextPage: number, nextPeriod: PeriodFilter) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const periodParam = getRankingPeriodParam(nextPeriod);
      const [rankingsResponse, myRankingResponse] = await Promise.all([
        getUserRankings({ page: nextPage, limit: LEADERBOARD_PAGE_SIZE, period: periodParam }),
        nextPage === 1 ? getMyUserRanking({ period: periodParam }) : Promise.resolve(null),
      ]);
      setTotal(rankingsResponse.meta.totalItems);
      setUsers((current) => (nextPage === 1 ? rankingsResponse.items : [...current, ...rankingsResponse.items]));
      setPage(nextPage);

      if (nextPage === 1) {
        setMyRanking(myRankingResponse?.item ?? null);
      }
    } catch (e: any) {
      setError(typeof e === "string" ? e : e?.message || "Failed to load leaderboard");
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    setUsers([]);
    setTotal(undefined);
    setPage(1);
    hasUserScrolledRef.current = false;
    loadLeaderboard(1, period);
  }, [loadLeaderboard, period]);

  React.useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || loading) return;

    const observer = new IntersectionObserver((entries) => {
      if (hasUserScrolledRef.current && entries.some((entry) => entry.isIntersecting)) {
        loadLeaderboard(page + 1, period);
      }
    }, { rootMargin: "240px" });

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadLeaderboard, loading, page, period]);

  React.useEffect(() => {
    const handleScroll = () => {
      hasUserScrolledRef.current = true;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="page-content leaderboard-page" data-cy="leaderboard-page">
      <Container fluid>
        <div className="leaderboard-heading">
          <div>
            <span className="leaderboard-heading__eyebrow">Arena ranking</span>
            <h1><i className="ri-trophy-line" aria-hidden="true" /> Leaderboard</h1>
          </div>
        </div>

        <div className="leaderboard-period-tabs" role="tablist" aria-label="Leaderboard period">
          {periodOptions.map((option) => (
            <button
              type="button"
              key={option.key}
              className={period === option.key ? "is-active" : ""}
              onClick={() => setPeriod(option.key)}
              data-cy={`leaderboard-period-${option.key}`}
            >
              {option.label}
            </button>
          ))}
        </div>

        <MyRankingCard ranking={myRanking} />

        {error && <Alert color="danger" role="alert">{error}</Alert>}

        <Row className="g-3">
          <Col xs={12}>
            <div className="leaderboard-list" aria-label="User leaderboard">
              {users.map((user, index) => (
                <LeaderboardRow user={user} key={`${getRank(user)}-${getDisplayName(user)}-${index}`} />
              ))}
            </div>
          </Col>
        </Row>

        {!loading && users.length === 0 && !error && (
          <div className="leaderboard-empty" data-cy="leaderboard-empty" data-testid="leaderboard-empty">No ranked users found</div>
        )}

        <div ref={sentinelRef} className="leaderboard-sentinel" data-cy="leaderboard-sentinel">
          {loading && (
            <span>
              <Spinner size="sm" /> Loading players...
            </span>
          )}
        </div>
      </Container>
    </div>
  );
};

export default Leaderboard;
